<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 80px)' : '1200px'"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Commissioning Report</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          class="mr-2"
          @click="showPreview = false"
        >
          <v-icon>mdi-eye-off</v-icon>
        </v-btn>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="commissioning-dialog-layout">
          <div :class="['commissioning-form-pane', showPreview ? 'with-preview' : '']">
            <div class="pa-6">
              <div class="section-title mb-2">Customer Information</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.customer_name"
                    label="Customer Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('customer_name')"
                    @input="clearValidationError('customer_name')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.invoice_number"
                    label="Invoice Number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('invoice_number') }"
                    @input="clearOptionalWarning('invoice_number')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.customer_address"
                    label="Customer Address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('customer_address')"
                    @input="clearValidationError('customer_address')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Commissioning</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="installationDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.installation_date"
                        label="Installation Date *"
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="getValidationError('installation_date')"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.installation_date"
                      no-title
                      scrollable
                      @input="onInstallationDateSelected"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="verifierDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.verifier_date"
                        label="Verifier Date *"
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="getValidationError('verifier_date')"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.verifier_date"
                      no-title
                      scrollable
                      @input="onVerifierDateSelected"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.installation_place"
                    label="Installation Place *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('installation_place')"
                    @input="clearValidationError('installation_place')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Asset Information</div>

              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.asset_name"
                    label="Asset Name *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    v-if="false"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.asset_type"
                    label="Asset Type *"
                    outlined
                    dense
                    :error-messages="getValidationError('asset_type')"
                    @input="clearValidationError('asset_type')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.warranty_period"
                    label="Warranty Period"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('warranty_period') }"
                    @input="clearOptionalWarning('warranty_period')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Handover Statement</div>

              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.handover_statement"
                    label="Statement *"
                    outlined
                    dense
                    rows="5"
                    auto-grow
                    :error-messages="getValidationError('handover_statement')"
                    @input="clearValidationError('handover_statement')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Items</div>

              <v-alert
                v-if="getValidationError('items').length"
                dense
                text
                type="warning"
                class="mb-3"
              >
                {{ getValidationError('items')[0] }}
              </v-alert>

              <v-row
                v-for="(item, index) in form.items"
                :key="'item-' + index"
                dense
                class="mb-2"
              >
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="item.name"
                    label="Name *"
                    hide-details="auto"
                    outlined
                    dense
                    :error-messages="getItemValidationError(index, 'name')"
                    @input="clearItemValidationError(index, 'name')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.quantity"
                    label="Quantity *"
                    outlined
                    hide-details="auto"
                    dense
                    :error-messages="getItemValidationError(index, 'quantity')"
                    @input="clearItemValidationError(index, 'quantity')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.unit"
                    label="Unit"
                    outlined
                    hide-details
                    dense
                    :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'unit') }"
                    @input="clearItemOptionalWarning(index, 'unit')"
                  />
                </v-col>

                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-btn icon color="red" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small outlined color="primary" class="mb-4" @click="addItem">
                <v-icon left>mdi-plus</v-icon>
                Add Item
              </v-btn>

              <div class="section-title mt-4 mb-2">Verification Data</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.verifier_name"
                    label="Verifier Name"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('verifier_name') }"
                    @input="clearOptionalWarning('verifier_name')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.verifier_statement"
                    label="Verifier Statement"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('verifier_statement') }"
                    @input="clearOptionalWarning('verifier_statement')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.customer_statement"
                    label="Customer Statement"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('customer_statement') }"
                    @input="clearOptionalWarning('customer_statement')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Issue</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.issue_place"
                    label="Issue Place *"
                    outlined
                    dense
                    :error-messages="getValidationError('issue_place')"
                    @input="clearValidationError('issue_place')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="issueDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.issue_date"
                        label="Issue Date *"
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :error-messages="getValidationError('issue_date')"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.issue_date"
                      no-title
                      scrollable
                      @input="onIssueDateSelected"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </div>
          </div>

          <div v-if="showPreview" class="commissioning-preview-pane">
            <div class="commissioning-preview-toolbar">
              <div class="subtitle-2 font-weight-medium">Preview</div>

              <v-progress-circular
                v-if="previewLoading"
                indeterminate
                size="18"
                width="2"
                color="primary"
              />
            </div>

            <iframe
              v-if="previewHtml"
              :srcdoc="previewHtml"
              class="commissioning-preview-frame"
              frameborder="0"
            />

            <div v-else class="commissioning-preview-empty">
              No preview available.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          text
          color="primary"
          :loading="previewLoading"
          @click="openPreview"
        >
          {{ showPreview ? "Hide Preview" : "Open Preview" }}
        </v-btn>

        <v-spacer />

        <v-btn text @click="closeDialog">Cancel</v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          @click="generateDocument"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";

const DOCUMENT_TYPE = "commissioning_protocol";

export default {
  name: "CommissioningProtocolDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    salesOrderId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      previewLoading: false,
      showPreview: false,
      previewHtml: "",
      previewDebounceTimer: null,
      previewRequestId: 0,
      validationErrors: {},
      optionalWarnings: {},
      installationDateMenu: false,
      verifierDateMenu: false,
      issueDateMenu: false,
      form: {
        customer_name: "",
        customer_address: "",
        installation_date: "",
        installation_place: "",
        asset_name: "",
        invoice_number: "",
        asset_type: "",
        warranty_period: "",
        items: [],
        handover_statement: "",
        verifier_date: "",
        verifier_name: "",
        verifier_statement: "",
        customer_statement: "",
        issue_place: "Zagyvarékas",
        issue_date: "",
      },
    };
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadDefaults();
      } else {
        this.resetPreview();
      }
    },

    form: {
      deep: true,
      handler() {
        if (!this.showPreview) {
          return;
        }

        this.schedulePreviewRefresh();
      },
    },
  },

  methods: {
    async loadDefaults() {
      try {
        const { data } = await ApiService.get(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/defaults`
        );

        this.form = {
          customer_name: data.customer_name || "",
          customer_address: data.customer_address || "",
          installation_date: data.installation_date || "",
          installation_place: data.installation_place || "",
          asset_name: data.asset_name || "",
          invoice_number: data.invoice_number || "",
          asset_type: data.asset_type || "",
          warranty_period: data.warranty_period || "",
          items: Array.isArray(data.items) ? data.items.filter(item => !String(item.name).includes('Szállítási díj')) : [],
          handover_statement: data.handover_statement || "",
          verifier_date: data.verifier_date || "",
          verifier_name: data.verifier_name || "",
          verifier_statement: data.verifier_statement || "",
          customer_statement: data.customer_statement || "",
          issue_place: data.issue_place || "Zagyvarékas",
          issue_date: data.issue_date || "",
        };

        this.validationErrors = {};
        this.optionalWarnings = {};
      } catch (e) {
        console.log(e);
      }
    },

    isEmpty(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    hasOptionalWarning(field) {
      return Boolean(this.optionalWarnings[field]);
    },

    hasItemOptionalWarning(index, field) {
      return Boolean(this.optionalWarnings[this.getItemValidationKey(index, field)]);
    },

    clearOptionalWarning(field) {
      if (this.optionalWarnings[field]) {
        this.$delete(this.optionalWarnings, field);
      }
    },

    clearItemOptionalWarning(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.optionalWarnings[key]) {
        this.$delete(this.optionalWarnings, key);
      }
    },

    getValidationError(field) {
      return this.validationErrors[field] || [];
    },

    getItemValidationKey(index, field) {
      return `items.${index}.${field}`;
    },

    getItemValidationError(index, field) {
      return this.validationErrors[this.getItemValidationKey(index, field)] || [];
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },

    clearItemValidationError(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.validationErrors[key]) {
        this.$delete(this.validationErrors, key);
      }
    },

    validateForm() {
      const errors = {};
      const requiredFields = [
        { key: "customer_name", label: "Customer Name" },
        { key: "customer_address", label: "Customer Address" },
        { key: "installation_date", label: "Installation Date" },
        { key: "verifier_date", label: "Verifier Date" },
        { key: "installation_place", label: "Installation Place" },
        { key: "asset_type", label: "Asset Type" },
        { key: "handover_statement", label: "Statement" },
        { key: "issue_place", label: "Issue Place" },
        { key: "issue_date", label: "Issue Date" },
      ];

      requiredFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          errors[field.key] = [`${field.label} is required.`];
        }
      });

      if (!Array.isArray(this.form.items) || this.form.items.length === 0) {
        errors.items = ["At least one item is required."];
      } else {
        this.form.items.forEach((item, index) => {
          [
            { key: "name", label: "Item name" },
            { key: "quantity", label: "Quantity" },
          ].forEach((field) => {
            if (this.isEmpty(item[field.key])) {
              errors[this.getItemValidationKey(index, field.key)] = [
                `${field.label} is required.`,
              ];
            }
          });
        });
      }

      this.validationErrors = errors;

      return Object.keys(errors).length === 0;
    },

    async showValidationAlert() {
      await Swal.fire({
        title: "Missing required data",
        icon: "warning",
        text: "Please fill in all required fields before creating the document.",
        confirmButtonText: "OK",
      });
    },

    getMissingOptionalFields() {
      const missingFields = [];
      const optionalFields = [
        { key: "invoice_number", label: "Invoice Number" },
        { key: "warranty_period", label: "Warranty Period" },
        { key: "verifier_name", label: "Verifier Name" },
        { key: "verifier_statement", label: "Verifier Statement" },
        { key: "customer_statement", label: "Customer Statement" },
      ];

      optionalFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          missingFields.push(field);
        }
      });

      if (Array.isArray(this.form.items)) {
        this.form.items.forEach((item, index) => {
          if (this.isEmpty(item.unit)) {
            missingFields.push({
              key: this.getItemValidationKey(index, "unit"),
              label: `Item ${index + 1} - Unit`,
            });
          }
        });
      }

      return missingFields;
    },

    markOptionalWarnings(fields) {
      const warnings = {};

      fields.forEach((field) => {
        warnings[field.key] = true;
      });

      this.optionalWarnings = warnings;
    },

    async confirmMissingOptionalFields() {
      const missingFields = this.getMissingOptionalFields();

      if (!missingFields.length) {
        this.optionalWarnings = {};
        return true;
      }

      const fieldList = missingFields
        .map((field) => `<li>${field.label}</li>`)
        .join("");

      const result = await Swal.fire({
        title: "Some fields are empty",
        icon: "warning",
        html: `The following fields are not filled:<br><ul style="text-align:left;margin-top:12px;">${fieldList}</ul><br>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Yes, generate document",
        cancelButtonText: "No, I will fill them",
        confirmButtonColor: "#00c300",
        width: "650px",
      });

      if (result.isConfirmed) {
        this.optionalWarnings = {};
        return true;
      }

      this.markOptionalWarnings(missingFields);
      return false;
    },

    onInstallationDateSelected() {
      this.installationDateMenu = false;
      this.clearValidationError("installation_date");
    },

    onVerifierDateSelected() {
      this.verifierDateMenu = false;
      this.clearValidationError("verifier_date");
    },

    onIssueDateSelected() {
      this.issueDateMenu = false;
      this.clearValidationError("issue_date");
    },

    addItem() {
      this.form.items.push({
        name: "",
        quantity: 1,
        unit: "db",
      });
    },

    removeItem(index) {
      this.form.items.splice(index, 1);
    },

    resetPreview() {
      this.showPreview = false;
      this.previewHtml = "";
      this.previewLoading = false;

      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
        this.previewDebounceTimer = null;
      }
    },

    closeDialog() {
      this.resetPreview();
      this.dialogProxy = false;
    },

    schedulePreviewRefresh() {
      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
      }

      this.previewDebounceTimer = setTimeout(() => {
        this.refreshPreview();
      }, 400);
    },

    async openPreview() {
        if(this.showPreview === true){
            this.showPreview = false;
        }else{
            this.showPreview = true;
        }

        await this.refreshPreview();
    },

    async refreshPreview() {
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/preview`,
          this.form
        );

        if (requestId !== this.previewRequestId) {
          return;
        }

        this.previewHtml = data.html || "";
      } catch (error) {
        console.log(error);
      } finally {
        if (requestId === this.previewRequestId) {
          this.previewLoading = false;
        }
      }
    },

    async generateDocument() {
      if (!this.validateForm()) {
        await this.showValidationAlert();
        return;
      }

      const missingFields = this.getMissingOptionalFields();

      this.markOptionalWarnings(missingFields);

      const canContinue = await this.confirmMissingOptionalFields();

      if (!canContinue) {
        return;
      }

      this.loading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/generate`,
          this.form
        );

        if (data && data.url) {
          window.open(data.url, "_blank");
        }

        this.$emit("generated", data);
        this.closeDialog();
      } catch (error) {
        console.error(error);

        Swal.fire({
          title: "Error",
          icon: "error",
          text: "The document could not be created.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.commissioning-dialog-layout {
  display: flex;
  min-height: 70vh;
}

.commissioning-form-pane {
  flex: 0 0 100%;
  max-width: 100%;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
}
.commissioning-form-pane.with-preview {
  flex: 0 0 40%;
  max-width: 40%;
}

.commissioning-preview-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  padding: 16px;
  min-width: 0;
}

.commissioning-preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.commissioning-preview-frame {
  width: 100%;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
}

.commissioning-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  color: #757575;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
}

.optional-warning-field >>> .v-input__slot {
  background-color: #fff8e1 !important;
}

.optional-warning-field >>> fieldset {
  border-color: #f9a825 !important;
}

</style>